import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { PlayCircle, X, Tag } from "lucide-react";
import PageHero from "../components/PageHero";
import Seo from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { api, resolveMedia } from "../lib/api";
import { useLang } from "../i18n";

function youtubeId(url) {
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([\w-]{6,})/);
  return m ? m[1] : null;
}

function thumb(v) {
  if (v.thumbnail) return resolveMedia(v.thumbnail);
  const id = youtubeId(v.url);
  return id ? `https://i.ytimg.com/vi/${id}/hqdefault.jpg` : null;
}

export default function Videos() {
  const { pick } = useLang();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(null);

  useEffect(() => {
    api.get("/videos").then((r) => setItems(r.data.items || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <main>
      <Seo
        title="वीडियो — हमारे कार्यों की झलक"
        description="Anansha Welfare Society की गतिविधियों और अभियानों के वीडियो — वाराणसी की सामाजिक पहल सीधे देखें।"
        path="/videos"
        breadcrumb={["वीडियो"]}
      />
      <PageHero
        overline={{ hi: "मीडिया", en: "Media" }}
        title={{ hi: "हमारे कार्यों की झलक — वीडियो", en: "Glimpses of Our Work — Videos" }}
        description={{ hi: "संस्था की गतिविधियों और अभियानों के वीडियो।", en: "Videos of the organization's activities and campaigns." }}
        testid="videos-page-heading"
      />
      <section className="py-20 bg-ngo-cream min-h-[40vh]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          {loading ? (
            <p className="text-center text-ngo-ink/50 py-16">...</p>
          ) : items.length === 0 ? (
            <div data-testid="videos-empty" className="text-center py-20">
              <PlayCircle size={44} className="mx-auto text-ngo-deep/40" />
              <p className="mt-4 text-ngo-ink/60">{pick({ hi: "वीडियो जल्द उपलब्ध होंगे।", en: "Videos will be available soon." })}</p>
            </div>
          ) : (
            <div data-testid="videos-grid" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {items.map((v, i) => (
                <Reveal key={v.id} i={i % 3}>
                  <button data-testid={`video-card-${i}`} onClick={() => setPlaying(v)} className="group w-full text-left bg-white rounded-2xl border border-ngo-deep/10 overflow-hidden hover:-translate-y-1.5 hover:shadow-[0_18px_44px_rgba(23,107,58,0.14)] transition-[box-shadow,transform] duration-300">
                    <div className="relative h-48 bg-ngo-forest overflow-hidden">
                      {thumb(v) ? (
                        <img src={thumb(v)} alt={v.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-ngo-gold"><PlayCircle size={44} /></div>
                      )}
                      <div className="absolute inset-0 bg-ngo-forest/20 group-hover:bg-ngo-forest/10 transition-colors duration-300" />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="w-14 h-14 rounded-full bg-ngo-gold text-ngo-ink flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                          <PlayCircle size={26} />
                        </span>
                      </div>
                    </div>
                    <div className="p-5">
                      {v.category && <p className="text-xs text-ngo-leaf font-semibold inline-flex items-center gap-1"><Tag size={11} /> {v.category}</p>}
                      <h3 className="mt-1.5 font-heading font-bold text-ngo-ink leading-snug">{v.title}</h3>
                      {v.description && <p className="mt-1.5 text-sm text-ngo-ink/60 line-clamp-2">{v.description}</p>}
                    </div>
                  </button>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      <AnimatePresence>
        {playing && (
          <motion.div
            data-testid="video-player-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] bg-ngo-forest/95 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setPlaying(null)}
            role="dialog"
            aria-modal="true"
          >
            <button data-testid="video-close" aria-label="बंद करें" className="absolute top-5 right-5 w-11 h-11 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 transition-colors duration-200" onClick={() => setPlaying(null)}>
              <X size={22} />
            </button>
            <div className="max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
              {youtubeId(playing.url) ? (
                <div className="aspect-video rounded-2xl overflow-hidden shadow-2xl">
                  <iframe
                    src={`https://www.youtube-nocookie.com/embed/${youtubeId(playing.url)}?autoplay=1&rel=0`}
                    title={playing.title}
                    className="w-full h-full border-0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              ) : (
                <div className="bg-white rounded-2xl p-10 text-center">
                  <p className="text-ngo-ink/70 mb-5">{playing.title}</p>
                  <a href={playing.url} target="_blank" rel="noopener noreferrer" data-testid="video-external-link" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-ngo-deep text-white font-bold">
                    {pick({ hi: "वीडियो खोलें", en: "Open Video" })}
                  </a>
                </div>
              )}
              <p className="mt-4 text-center text-ngo-mint/90 font-medium">{playing.title}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
